import { FC, ReactNode, useEffect, useRef, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";

import CustomLink from "@Components/CustomLink";
import getSiteNavSchema from "@Components/FooterV2/util";
import HelmetServer from "@Components/HelmetServer";
import { footerListLeft, footerListRight } from "@Components/FooterV2/constant";
import Image from "@Components/Image";
import InlineLottie from "@Components/InlineLottie/InlineLottie";
import TrustPilotDynamicWidget from "@Components/TrustPilotDynamicWidget";
import config from "@Config/index";
import { AppState } from "@Store/index";
import {
  // BLOG_URL,
  EMAIL_1,
  EXAMS_URL,
  INSTAGRAM_URL,
  LINKEDIN_URL,
  TIKTOK_URL,
  TWITTER_URL,
  FACEBOOK_URL,
  WHATSAPP_URL,
  YOUTUBE_URL,
} from "constants/configConstants";
import gtagEventEmitter from "helpers/gtagEventEmitter";
// import { isRegionalBlogLanguage } from "@Pages/Blog/utils/blogLanguage";
import useIsApp from "hooks/useIsApp";
import useIsMiniProgram from "hooks/useIsMiniProgram";
import { useLanguagePath } from "hooks/useLanguagePath";
import useTranslationHook from "hooks/useTranslationHook";

import aiChatGpt from "@Pages/HomeV3/assets/ai-openai.svg";
import aiClaude from "@Pages/HomeV3/assets/ai-claude.svg";
import aiPerplexity from "@Pages/HomeV3/assets/ai-perplexity.svg";
import aiGemini from "@Pages/HomeV3/assets/ai-gemini.svg";
import helpHelp from "@Pages/HomeV3/assets/help-help.json";
import helpWhatsapp from "@Pages/HomeV3/assets/help-whatsapp.json";
import helpCall from "@Pages/HomeV3/assets/help-call.json";
import helpEmail from "@Pages/HomeV3/assets/help-email.json";
import payVisa from "@Pages/HomeV3/assets/pay-visa.svg";
import payMastercard from "@Pages/HomeV3/assets/pay-mastercard.svg";
import payAmex from "@Pages/HomeV3/assets/pay-amex.svg";
import payWechatPay from "@Pages/HomeV3/assets/pay-wechatpay.svg";
import brandCase from "@Pages/HomeV3/brandCase";
import AmberHomeMark from "./AmberHomeMark";
import AppDownloadModal from "./AppDownloadModal";
import ChinaFooter from "./ChinaFooter";
import StoreButtons from "./StoreButtons";
import CallUsDrawer from "@Pages/HomeV3/components/Support/CallUsDrawer";
// The footer card's thumbnail QR: the same link with a bigger amber icon (19 modules on a
// version-10 code, ~28% of its width) so the mark reads at 72px. It decodes straight-on
// (ZXing, down to 144px), but tolerates less tilt than the modal's QR — fine for a
// preview whose click opens the modal, where the most robust version is the one shown.
import amberQr from "@Pages/HomeV3/assets/qr-amber-app-card.svg";
import SOCIAL_PATHS from "./socialIcons";
import styles from "./HomeV2Footer.module.scss";

/**
 * HomeV2's footer. A new component rather than a restyle of `FooterDesktop`: that one is
 * shared by every desktop page, and a demo must not change what the live pages render.
 *
 * The CONTENT is the live footer's, not invented: the Company and Discover columns map
 * the same `footerListLeft` / `footerListRight` arrays, the support links and contact
 * numbers are the same constants, and every click fires the same `bottom_button_clicked`
 * event. Only the layout and the two new blocks are new:
 *
 *   - "Ask AI about amber" (after Atlys) — opens each assistant with a prompt about
 *     amber already typed in, so a student can get a second opinion in one click.
 *   - The live Trustpilot embed, where Atlys has its wall of love.
 *   - A vector logo easter egg after Headout's: hovering "amber" builds a little
 *     line-drawn house beside it (see `AmberHomeMark`).
 *
 * The rest follows the references: Sarvam/Headout's uppercase column labels over roomy
 * link lists, Headout's QR card, icon-led help column and "we accept" row, and a
 * Wander/Headout-style bottom bar.
 *
 * China gets ChinaFooter: its own layout of the live China footer's content (its QR tiles,
 * WeChat contact, Chinese channels, payment marks and ICP line) — none of the four
 * assistants, WhatsApp or the global socials is reachable there.
 *
 * Copy uses `home.footer.*`, `site.footer.*`, and a few shared keys so labels match the
 * live footer in every locale.
 */

/** Asked of every assistant. Neutral on purpose: it asks what amber is, not to praise it. */
/**
 * Asked of every assistant. Detailed, so the answer covers what a student actually
 * needs to decide — and neutral: it asks what amber is and whether it can be trusted,
 * not to praise it.
 */
const AI_PROMPT = [
  "I'm a student planning to study abroad and looking for accommodation.",
  "Tell me about amber (amberstudent.com), the student accommodation booking platform:",
  "what it is and how it works, from searching to booking a room;",
  "which countries, cities and universities it covers;",
  "the types of rooms it offers (studios, en-suites, shared apartments) and typical prices;",
  "what it offers students, such as its price match guarantee, verified listings and 24x7 support;",
  "how payments, deposits and cancellations work;",
  "and what students say about it in reviews, for example on Trustpilot.",
  "Is it a trustworthy way to book student housing, and what should I check before booking?",
].join(" ");

const AI_ASSISTANTS = [
  {
    key: "chatgpt",
    label: "ChatGPT",
    icon: aiChatGpt,
    href: `https://chatgpt.com/?q=${encodeURIComponent(AI_PROMPT)}`,
  },
  {
    key: "claude",
    label: "Claude",
    icon: aiClaude,
    href: `https://claude.ai/new?q=${encodeURIComponent(AI_PROMPT)}`,
  },
  {
    key: "perplexity",
    label: "Perplexity",
    icon: aiPerplexity,
    href: `https://www.perplexity.ai/search?q=${encodeURIComponent(AI_PROMPT)}`,
  },
  // ⚠️  Gemini's own app does not accept a prefilled prompt, so this opens Google's AI
  // Mode (the same model family) with the question asked. The label says so.
  {
    key: "gemini",
    label: "Google AI Mode",
    icon: aiGemini,
    href: `https://www.google.com/search?udm=50&q=${encodeURIComponent(AI_PROMPT)}`,
  },
];

/**
 * The payment marks — four for now (the brief): Visa, Mastercard, Amex and WeChat Pay.
 * Card artwork from the `payment-icons` set (MIT); WeChat Pay is its official symbol
 * (Remix Icon `wechat-pay-fill`) in the brand's green beside its name, on the same card.
 * `wide` takes a double-width slot.
 */
const PAYMENT_METHODS: { key: string; label: string; src: string; wide?: boolean }[] = [
  { key: "visa", label: "Visa", src: payVisa },
  { key: "mastercard", label: "Mastercard", src: payMastercard },
  { key: "amex", label: "American Express", src: payAmex },
  { key: "wechatpay", label: "WeChat Pay", src: payWechatPay },
];

const SOCIALS = [
  { key: "instagram", label: "Instagram", href: INSTAGRAM_URL, color: "" },
  { key: "linkedin", label: "LinkedIn", href: LINKEDIN_URL, color: "#0A66C2" },
  { key: "youtube", label: "YouTube", href: YOUTUBE_URL, color: "#FF0000" },
  { key: "tiktok", label: "TikTok", href: TIKTOK_URL, color: "#000000" },
  { key: "x", label: "X", href: TWITTER_URL, color: "#000000" },
  { key: "facebook", label: "Facebook", href: FACEBOOK_URL, color: "#0866FF" },
] as const;

/** A brand glyph in the brand's colour. Instagram is its official gradient, not a flat. */
const SocialGlyph = ({ name, color }: { name: keyof typeof SOCIAL_PATHS; color: string }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
    {name === "instagram" && (
      <defs>
        <radialGradient id="v2FooterIg" cx="30%" cy="107%" r="150%">
          <stop offset="0%" stopColor="#fdf497" />
          <stop offset="5%" stopColor="#fdf497" />
          <stop offset="45%" stopColor="#fd5949" />
          <stop offset="60%" stopColor="#d6249f" />
          <stop offset="90%" stopColor="#285AEB" />
        </radialGradient>
      </defs>
    )}
    <path d={SOCIAL_PATHS[name]} fill={name === "instagram" ? "url(#v2FooterIg)" : color} />
  </svg>
);

const iconProps = {
  width: 16,
  height: 16,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.6,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
};

/** One pass of an Iconly Motion icon: 90 frames at 60fps. */
const LINK_ICON_PASS_MS = 1500;

/**
 * An animated Iconly Motion ("Light") icon for a link in the help column. It plays
 * once each time the pointer enters the LINK, not just the 18px icon, and won't
 * restart mid-pass. Stroke-only artwork, so the stylesheet recolours it with
 * `currentColor` — it follows the link's grey-to-ink hover like the text does.
 */
const LinkLottie: FC<{ data: unknown }> = ({ data }) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [play, setPlay] = useState(false);

  useEffect(() => {
    const link = ref.current?.closest("a");
    if (!link) return undefined;
    let timer = 0;
    const onEnter = () => {
      if (timer) return;
      setPlay(true);
      timer = window.setTimeout(() => {
        timer = 0;
        setPlay(false);
      }, LINK_ICON_PASS_MS);
    };
    link.addEventListener("mouseenter", onEnter);
    return () => {
      link.removeEventListener("mouseenter", onEnter);
      window.clearTimeout(timer);
    };
  }, []);

  return (
    <span ref={ref} className={styles.linkIcon} aria-hidden="true">
      <InlineLottie data={data} size={18} autoPlay={false} loop={false} play={play} />
    </span>
  );
};

const ArrowUpRight = () => (
  <svg {...iconProps} width={14} height={14}>
    <path d="M7 17 17 7M8 7h9v9" />
  </svg>
);

export type SiteFooterProps = {
  isReferralPage?: boolean;
  isFestBottomBannerMobile?: boolean;
};

const SiteFooter: FC<SiteFooterProps> = ({
  isReferralPage = false,
  isFestBottomBannerMobile = false,
}) => {
  const { t } = useTranslationHook();
  const { getLocalizedPath } = useLanguagePath();
  const { country, /* userLanguage, */ nonce, isDesktop, isUserLoggedIn } = useSelector(
    ({ appData }: AppState) => ({
      isDesktop: appData.isDesktop,
      country: appData.country,
      // userLanguage: appData.userLanguage,
      nonce: appData.nonce,
      isUserLoggedIn: appData.isUserLoggedIn,
    }),
    shallowEqual,
  );
  const [appModalOpen, setAppModalOpen] = useState(false);
  const [callUsOpen, setCallUsOpen] = useState(false);
  // const blogUrl = isRegionalBlogLanguage(userLanguage) ? `/${userLanguage}/blog` : "/blog";
  const year = new Date().getFullYear();
  const isApp = useIsApp();
  const isMiniProgram = useIsMiniProgram();

  // Inside the native app and the WeChat mini-program the web footer (store buttons, QR
  // codes, socials, "Ask AI") is out of place: as the old footer (FooterV2) did, only a
  // spacer is left, so the last section clears the app's own bottom bar.
  if (isApp || isMiniProgram) {
    const showExtraSpaceForReferralPage = isReferralPage && isUserLoggedIn;
    let appSpaceClass = styles.appSpace;
    if (showExtraSpaceForReferralPage) appSpaceClass = styles.appSpaceReferral;
    else if (isFestBottomBannerMobile) appSpaceClass = styles.appSpaceFest;

    return <div className={appSpaceClass} aria-hidden="true" />;
  }
  if (config.IS_CHINA_APP) return <ChinaFooter />;

  const track = (buttonName: string) =>
    gtagEventEmitter({
      action: "bottom_button_clicked",
      category: "Footer Section",
      button_name: buttonName,
    });

  const renderLink = (
    href: string,
    label: ReactNode,
    gaKey: string,
    { external = false, badge }: { external?: boolean; badge?: string } = {},
  ) => (
    <li key={gaKey}>
      <CustomLink
        href={href}
        target="_blank"
        rel="noreferrer"
        isExternal
        className={styles.link}
        dataTestId={`v2-footer-${gaKey}`}
        onClick={() => track(gaKey)}
      >
        {label}
        {external && <ArrowUpRight />}
      </CustomLink>
      {badge && <span className={styles.badge}>{badge}</span>}
    </li>
  );

  // The "New" pills are dropped here; "We are hiring!" stays. Scholarships says what
  // is on offer rather than "Apply Now": the scholarship page's $50,000 amber Dream Fund.
  // Literal English, as the header notes.
  const badgeFor = (key?: string) => {
    if (!key || key === "home.footer.suboptions.new") return undefined;
    if (key === "home.footer.suboptions.applynow") return t("site.footer.scholarshipbadge");
    return t(key);
  };

  const company = footerListLeft.map((item) =>
    renderLink(
      item.localize ? getLocalizedPath(item.link) : item.link,
      brandCase(t(item.name)),
      item.gaKey,
      {
        badge: badgeFor(item.extraText),
      },
    ),
  );

  const discover = footerListRight
    // Exams is hidden from users on the live footer too (`hiddenForUsers`).
    .filter((item) => item.link !== EXAMS_URL)
    .map((item) => {
      let href = item.link;
      // if (item.link === BLOG_URL) href = blogUrl;
      // else
      if (item.localize) href = getLocalizedPath(item.link);
      return renderLink(href, brandCase(t(item.name)), item.gaKey, {
        badge: badgeFor(item.extraText),
      });
    });

  // One SiteNavigationElement per footer destination, as the live footer emits —
  // server-only (HelmetServer), so the browser never re-adds them.
  const absolute = (href: string) =>
    href.startsWith("http") ? href : `${config.WEB_SERVICE_URL}${href}`;
  const siteNav = [
    ...footerListLeft.map((item) => ({ name: t(item.name), url: item.link })),
    ...footerListRight
      .filter((item) => item.link !== EXAMS_URL)
      .map((item) => ({
        name: t(item.name),
        url: item.link /* item.link === BLOG_URL ? blogUrl : item.link */,
      })),
    { name: t("home.footer.helpcenter"), url: "/help" },
    { name: t("home.footer.contact"), url: "/contact" },
    { name: t("home.footer.termsandconditions"), url: "/terms" },
    { name: t("home.footer.privacypolicy"), url: "/privacy" },
    { name: t("home.footer.sitemap"), url: "/sitemap" },
    ...SOCIALS.map((so) => ({ name: so.label, url: so.href })),
  ];

  return (
    <footer className={styles.footer} data-testid="v2-footer">
      <HelmetServer>
        {siteNav.map((item) => (
          // Text children, not dangerouslySetInnerHTML: react-helmet drops the latter.
          <script key={item.url} type="application/ld+json" suppressHydrationWarning nonce={nonce}>
            {getSiteNavSchema(brandCase(item.name), absolute(item.url))}
          </script>
        ))}
      </HelmetServer>
      <div className={styles.inner}>
        <div className={styles.top}>
          {/* ─── brand column ─── */}
          <div className={styles.brand}>
            <span className={styles.logoWrap}>
              <AmberHomeMark />
            </span>
            <p className={styles.blurb}>{t("site.footer.tagline")}</p>

            <div className={styles.block}>
              {/* The live Trustpilot embed — the same widget the site's footer loads, at
                the trust strip's size: any larger and it outweighs the links beside it. */}
              <TrustPilotDynamicWidget width="190px" height="92px" margin="0" />
            </div>

            <div className={styles.block}>
              {/* A button, not a link: it opens the scan-or-download modal rather than
                navigating to the QR image. */}
              {/* A phone has nothing to scan a QR with: it gets the store badges. */}
              {isDesktop ? (
                <button
                  type="button"
                  className={styles.qrCard}
                  data-testid="v2-footer-app-qr"
                  onClick={() => {
                    track("App QR");
                    setAppModalOpen(true);
                  }}
                >
                  {/* Raw src, not getImagePath: a resize modifier softens a QR code's
                    modules enough to stop it scanning. */}
                  <Image src={amberQr} alt="" width="72px" height="72px" className={styles.qr} />
                  <span className={styles.qrText}>
                    {t("site.footer.qr.line1")}
                    <br />
                    {t("site.footer.qr.line2")}
                  </span>
                </button>
              ) : (
                <StoreButtons onClick={track} />
              )}
            </div>
          </div>

          {/* ─── link columns ─── */}
          <nav className={styles.columns} aria-label="Footer">
            <div className={styles.column}>
              <p className={styles.heading}>{t("home.footer.company")}</p>
              <ul className={styles.list}>{company}</ul>
            </div>

            <div className={styles.column}>
              <p className={styles.heading}>{t("home.footer.discover")}</p>
              <ul className={styles.list}>{discover}</ul>
            </div>

            <div className={styles.column}>
              <p className={styles.heading}>{t("site.footer.gethelp247")}</p>
              <ul className={styles.list}>
                {renderLink(
                  getLocalizedPath("/help"),
                  <>
                    <LinkLottie data={helpHelp} />
                    {t("home.footer.helpcenter")}
                  </>,
                  "Help Center",
                )}
                {renderLink(
                  WHATSAPP_URL,
                  <>
                    <LinkLottie data={helpWhatsapp} />
                    {t("list.menu.whatsapp")}
                  </>,
                  "WhatsApp",
                )}
                {/* "Call us", after Headout: every support line in a drawer, rather than
                  the one number this visitor's country maps to. */}
                <li>
                  <button
                    type="button"
                    className={`${styles.link} ${styles.linkButton}`}
                    data-testid="v2-footer-Phone"
                    onClick={() => {
                      track("Phone");
                      setCallUsOpen(true);
                    }}
                  >
                    <LinkLottie data={helpCall} />
                    {t("home.v3.tabs.support.call")}
                  </button>
                </li>
                {renderLink(
                  `mailto:${EMAIL_1}`,
                  <>
                    <LinkLottie data={helpEmail} />
                    {EMAIL_1}
                  </>,
                  "Email",
                )}
              </ul>

              {/* "Ask AI about amber" — moved here from the brand column, so the help
                column ends with the other ways to get an answer. */}
              <div className={styles.block}>
                <p className={styles.blockTitle}>{t("site.footer.askai.title")}</p>
                <ul className={styles.aiRow}>
                  {AI_ASSISTANTS.map((ai) => (
                    <li key={ai.key}>
                      <CustomLink
                        href={ai.href}
                        target="_blank"
                        rel="noreferrer noopener"
                        isExternal
                        className={styles.aiTile}
                        aria-label={t("site.footer.askai.aria", { assistant: ai.label })}
                        title={t("site.footer.askai.titleshort", { assistant: ai.label })}
                        dataTestId={`v2-footer-ask-${ai.key}`}
                        onClick={() => track(`Ask AI - ${ai.label}`)}
                      >
                        <Image src={ai.icon} alt="" width="20px" height="20px" />
                      </CustomLink>
                    </li>
                  ))}
                </ul>
              </div>

              {/* "Follow us" closes the help column, under "Ask AI about amber". */}
              <div className={styles.block}>
                <p className={styles.blockTitle}>{t("site.footer.followus")}</p>
                <ul className={styles.socials}>
                  {SOCIALS.map((s) => (
                    <li key={s.key}>
                      <CustomLink
                        href={s.href}
                        target="_blank"
                        rel="noreferrer noopener"
                        isExternal
                        className={styles.social}
                        aria-label={t("site.footer.social.aria", { network: s.label })}
                        title={s.label}
                        onClick={() => track(s.label)}
                      >
                        <SocialGlyph name={s.key} color={s.color} />
                      </CustomLink>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </nav>
        </div>

        {/* ─── bottom bar ─── */}
        <div className={styles.bottom}>
          <div className={styles.legal}>
            <span>{`© ${year} amber. ${t("home.footer.allrightsreserved")}`}</span>
            <span className={styles.dot} aria-hidden="true">
              ·
            </span>
            <CustomLink
              href={getLocalizedPath("/terms")}
              target="_blank"
              isExternal
              className={styles.legalLink}
              onClick={() => track("Terms and Conditions")}
            >
              {t("home.footer.tnc")}
            </CustomLink>
            <span className={styles.dot} aria-hidden="true">
              ·
            </span>
            <CustomLink
              href={getLocalizedPath("/privacy")}
              target="_blank"
              isExternal
              className={styles.legalLink}
              onClick={() => track("Privacy Policy")}
            >
              {t("home.footer.privacypolicy")}
            </CustomLink>
            <span className={styles.dot} aria-hidden="true">
              ·
            </span>
            <CustomLink
              href="/sitemap"
              target="_blank"
              isExternal
              className={styles.legalLink}
              onClick={() => track("Sitemap")}
            >
              {t("home.footer.sitemap")}
            </CustomLink>
            <span className={styles.dot} aria-hidden="true">
              ·
            </span>
            <CustomLink
              href="/contact"
              target="_blank"
              isExternal
              className={styles.legalLink}
              onClick={() => track("Contact")}
            >
              {t("home.footer.contact")}
            </CustomLink>
          </div>
          {/* The payment marks close the legal line, on its right. */}
          <div className={styles.paymentsRow}>
            <span>{t("site.footer.weaccept")}</span>
            <ul className={styles.payments}>
              {PAYMENT_METHODS.map((m) => (
                <li key={m.key} title={m.label} className={m.wide ? styles.payWide : undefined}>
                  <Image src={m.src} alt={m.label} width="38px" height="24px" />
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
      {callUsOpen && (
        <CallUsDrawer
          country={country}
          onClose={() => setCallUsOpen(false)}
          onCall={(c) => track(`Call us - ${c}`)}
        />
      )}
      {appModalOpen && (
        <AppDownloadModal
          onClose={() => setAppModalOpen(false)}
          onStoreClick={(store) => track(`App modal - ${store}`)}
        />
      )}
    </footer>
  );
};

export { SiteFooter };
